import { metricKeys } from '@pulse/metrics';
import { BASE_METRICS } from '@utils/metrics';
import { CURRENT_PRODUCT_MODE, isAppModule } from '@utils/constants';
import {
  getPercentageMetricByKey,
  combineWithMetricDeDuplication,
} from '@utils/metrics/metricHelpers';

export const modes = {
  TRENDS: 'trends',
  FILTERS: 'filters',
};

export const unsavedChangesTextObj = {
  title: 'Save Changes',
  text: 'Leaving this page without saving will discard any new changes. Do you want to save these changes?',
};

const { baseUrl } = window.conviva;
export const NOC_URL = `${baseUrl}/dashboard/real-time`;

export const DEFAULT_NOC_METRIC_SORT = {
  EI: { metric: metricKeys.ConcurrentPlays, order: 'desc' },
  AI: { metric: metricKeys.Plays, order: 'desc' },
  APP: { metric: metricKeys.AverageRequestDuration, order: 'desc' },
  ODP: { metric: '', order: '' },
};

export const DEFAULT_SORT = {
  key: DEFAULT_NOC_METRIC_SORT[CURRENT_PRODUCT_MODE].metric,
  order: DEFAULT_NOC_METRIC_SORT[CURRENT_PRODUCT_MODE].order,
};

export const DEFAULT_NOC_METRICS = {
  EI: [metricKeys.Plays, metricKeys.Attempts],
  AI: [metricKeys.Plays, metricKeys.Attempts],
  APP: [metricKeys.AppUniqueDevices],
  ODP: [],
};

export const DEFAULT_DIMENSION = {
  EI: 'OBJECT_NAME',
  AI: 'OBJECT_NAME',
  APP: 'browser_name',
  ODP: '',
};

export const AI_ALERT = isAppModule ? 'APP_AI_ALERTS' : 'AI_ALERTS';

export enum ERROR_THRESHOLD_TYPE {
  OUT_OF_RANGE = 'OUT_OF_RANGE',
  GENERAL = 'GENERAL',
  LESS_EQUAL_ZERO = 'LESS_EQUAL_ZERO',
}

export const ERROR_MSG = {
  [ERROR_THRESHOLD_TYPE.OUT_OF_RANGE]: 'Value should be between 0 and 100',
  [ERROR_THRESHOLD_TYPE.GENERAL]: 'Value must be numeric',
  [ERROR_THRESHOLD_TYPE.LESS_EQUAL_ZERO]: 'Value should be greater than 0',
};

export const percentageValidation = (v: number) => ({
  type: ERROR_THRESHOLD_TYPE.OUT_OF_RANGE,
  pass: v > 0 && v < 100,
});

export const isInstantFilter = (filter) => {
  return (filter?.fid === undefined || filter?.fid === null) && filter?.name !== 'All Traffic';
};

const AUDIENCE_METRIC_MAP = {
  EI: {
    [metricKeys.Attempts]: [
      metricKeys.HighExitBeforeVideoStart,
      metricKeys.VideoStartFailures,
      metricKeys.ExitBeforeVideoStarts,
      metricKeys.VideoStartFailuresBusiness,
      metricKeys.VideoStartFailuresTech,
      metricKeys.ExitsDuringPreRoll,
    ],
    [metricKeys.ConcurrentPlays]: [
      metricKeys.IntervalMinutesPlayed,
      metricKeys.Framerate,
      metricKeys.AvgBitrate,
      metricKeys.Bitrate,
      metricKeys.EndingBitrate,
      metricKeys.NonZeroCIRREndedPlays,
      metricKeys.ConnectionInducedRebufferingRatio,
      metricKeys.RebufferingRatio,
      metricKeys.VideoRestartTime,
    ],
    [metricKeys.EndedPlays]: [
      metricKeys.PercentageComplete,
      metricKeys.MinutesPerEndedPlay,
      metricKeys.MinutesPerUniqueDevice,
      metricKeys.Minutes,
      metricKeys.HighRebuffering,
      metricKeys.LowBitrate,
      metricKeys.VideoPlaybackFailures,
      metricKeys.VideoPlaybackFailuresTech,
      metricKeys.LifeJoinedAvgBitrate,
      metricKeys.LifeAvgNumBitrateSwitches,
      metricKeys.ExitDueToCIR,
      metricKeys.PausedRatio,
      metricKeys.PausedTime,
      metricKeys.VideoPlaybackFailuresBusiness,
      metricKeys.ZeroCIRREndedPlays,
    ],
    [metricKeys.Plays]: [metricKeys.HighStartupTime, metricKeys.VideoStartTime],
    [metricKeys.SpiStreams]: [metricKeys.BadSession, metricKeys.StreamingPerformanceIndex],
    // Hide ad metric on video threshold config due limitation in threshold service.
    // [metricKeys.AdEndedPlays]: [
    //   metricKeys.AdActualDuration,
    //   metricKeys.AdBitrate,
    //   metricKeys.AdEndedPlaysPerUniqueDevice,
    //   metricKeys.AdMinutesPlayedPerUniqueDevice,
    //   metricKeys.AdVideoPlaybackFailures,
    //   metricKeys.AdRebufferingRatio,
    // ],
    // [metricKeys.AdAttempts]: [
    //   metricKeys.AdVideoStartFailures,
    //   metricKeys.AdVideoStartTime,
    //   metricKeys.AdExitBeforeVideoStarts,
    // ],
    // [metricKeys.AdPlays]: [metricKeys.AdCompletedCreativePlays],
  },
  AI: {
    [metricKeys.ConcurrentPlays]: [metricKeys.RebufferingRatio, metricKeys.Bitrate],
    [metricKeys.Attempts]: [metricKeys.VideoStartFailuresPercentage, metricKeys.VideoStartTime],
    [metricKeys.Plays]: [
      metricKeys.AdSlateDurationPct,
      metricKeys.AdSlatePlaysPct,
      metricKeys.AdCompletedCreativePlays,
    ],
    [metricKeys.EndedPlays]: [
      metricKeys.AdActualDuration,
      metricKeys.MinutesPerUniqueDevice,
      metricKeys.EndedPlaysPerUniqueDevice,
      metricKeys.VideoPlaybackFailures,
    ],
  },
  APP: {
    [metricKeys.DocumentLoads]: [metricKeys.DocumentLoadTime],
    [metricKeys.AppScreenLoads]: [metricKeys.AppAvgScreenLoadTime],
    [metricKeys.AverageRequestCount]: [metricKeys.AverageRequestDuration],
    [metricKeys.AppUniqueDevices]: [metricKeys.AppUniqueDevices],
  },
};

export const getAudienceMetricThresholdMap = () => {
  const metricMap = AUDIENCE_METRIC_MAP[CURRENT_PRODUCT_MODE] || {};

  const thresoldMetricMap = {} as Record<
    string,
    {
      metrics: string[];
      percentageMetrics: string[];
    }
  >;

  Object.keys(metricMap).forEach((key) => {
    const percentageMetrics = (metricMap[key] || []).map((it) =>
      getPercentageMetricByKey({
        metricKey: it,
        defaultValue: it,
      }),
    );
    thresoldMetricMap[key] = {
      metrics: metricMap[key],
      percentageMetrics,
    };
  });

  return thresoldMetricMap;
};

export const getAllBaseLineMetrics = () => {
  const metricMap = AUDIENCE_METRIC_MAP[CURRENT_PRODUCT_MODE] || {};
  return combineWithMetricDeDuplication(Object.keys(metricMap), BASE_METRICS);
};

export const getAllPercentageMetrics = () => {
  const thresholdMap = getAudienceMetricThresholdMap();

  const allPercentageMetrics = [] as string[];

  Object.values(thresholdMap).map((it) => {
    allPercentageMetrics.push(...it.percentageMetrics);
  });
  return allPercentageMetrics;
};

export const findMatchedBaseLineMetric = (metricName: string) => {
  const thresholdMap = getAudienceMetricThresholdMap();
  const metricToBaselineMap = Object.entries(thresholdMap).reduce(
    (map, [baseline, data]) => {
      const allMetrics = [...data.metrics, ...data.percentageMetrics];
      allMetrics.forEach((it) => {
        map[it] = baseline;
      });
      return map;
    },
    {} as Record<string, string>,
  );
  return metricToBaselineMap[metricName];
};
